import React, { useState, useEffect } from 'react';
import { getAllPosts, updatePost, createPost, deletePost, getPostById, getSettings, updateSettings } from './blogService';
import './Admin.css';

const Admin = ({ onClose }) => {
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    featured_image: '',
    post_type: 'article',
    tags: '',
    spotify_embed: '',
    link_url: '',
    video_url: '',
    created_at: ''
  });

  useEffect(() => {
    loadPosts();
    getSettings().then((settings) => {
      if (settings.logo_url) setLogoUrl(settings.logo_url);
    });
  }, []);

  // Load every article. featured_image is included in the list query (image
  // URLs are small), so all posts can be shown at once.
  const loadPosts = async () => {
    const data = await getAllPosts();
    setPosts(data);
  };

  const handleSaveLogo = async () => {
    setSavingLogo(true);
    const ok = await updateSettings({ logo_url: logoUrl });
    setSavingLogo(false);
    alert(ok ? 'Logo updated! Refresh the site to see it.' : 'Failed to update logo');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Ensure at least something is provided
    if (!formData.title && !formData.content && !formData.featured_image) {
      alert('Please add at least a title, content, or image');
      return;
    }
    
    // Set default title if empty
    const postData = {
      ...formData,
      title: formData.title || 'Untitled',
      content: formData.content || ''
    };
    
    try {
      if (editingPost) {
        // Update existing post
        const result = await updatePost(editingPost.id, postData);
        if (!result) throw new Error('Failed to update post');
        alert('Post updated!');
      } else {
        // Create new post
        const result = await createPost(postData);
        if (!result) throw new Error('Failed to create post');
        alert('Post created!');
      }
      
      // Reset form
      setFormData({ title: '', content: '', featured_image: '', post_type: 'article', tags: '', spotify_embed: '', link_url: '', video_url: '', created_at: '' });
      setEditingPost(null);
      loadPosts();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleEdit = async (post) => {
    // Fetch full post content since list only has summary fields
    const fullPost = await getPostById(post.id);
    if (fullPost) {
      setEditingPost(fullPost);
      setFormData({
        title: fullPost.title,
        content: fullPost.content || '',
        featured_image: fullPost.featured_image || '',
        post_type: fullPost.post_type || 'article',
        tags: fullPost.tags || '',
        spotify_embed: fullPost.spotify_embed || '',
        link_url: fullPost.link_url || '',
        video_url: fullPost.video_url || '',
        created_at: fullPost.created_at || ''
      });
    } else {
      // Fallback to summary data if fetch fails
      setEditingPost(post);
      setFormData({
        title: post.title,
        content: post.content || '',
        featured_image: post.featured_image || '',
        post_type: post.post_type || 'article',
        tags: post.tags || '',
        spotify_embed: post.spotify_embed || '',
        link_url: post.link_url || '',
        video_url: post.video_url || '',
        created_at: post.created_at || ''
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    
    try {
      const result = await deletePost(id);
      if (!result) throw new Error('Failed to delete post');
      alert('Post deleted!');
      loadPosts();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  return (
    <div className="admin-overlay">
      <div className="admin-panel">
        <div className="admin-header">
          <h2>Admin Panel</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        
        {/* Site Settings: change the main logo */}
        <div className="logo-settings">
          <h3>Site Logo</h3>
          <p className="logo-hint">Paste an image URL to use as the main logo.</p>
          <div className="logo-settings-row">
            {logoUrl && <img src={logoUrl} alt="Logo preview" className="logo-preview" />}
            <input
              type="text"
              placeholder="Logo image URL (e.g. https://...png)"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
            <button type="button" onClick={handleSaveLogo} disabled={savingLogo} className="edit-btn">
              {savingLogo ? 'Saving...' : 'Save Logo'}
            </button>
          </div>
        </div>

        <div className="admin-content">
          {/* Form */}
          <div className="admin-form">
            <h3>{editingPost ? 'Edit Post' : 'New Post'}</h3>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Title (optional)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              
              <label className="field-label">Image link (paste a URL)</label>
              <input
                type="text"
                placeholder="Image URL (e.g. https://...jpg)"
                value={formData.featured_image}
                onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
              />
              {formData.featured_image && (
                <div style={{ marginTop: '-8px', marginBottom: '8px' }}>
                  <img src={formData.featured_image} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }} />
                  <button type="button" onClick={() => setFormData({ ...formData, featured_image: '' })} style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '12px' }}>Remove</button>
                </div>
              )}

              <textarea
                placeholder="Content (optional - HTML allowed)"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows="8"
              />
              
              <input
                type="text"
                placeholder="Tags (comma separated: music, art, thoughts)"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
              
              <input
                type="datetime-local"
                label="Date"
                value={formData.created_at ? formData.created_at.slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, created_at: e.target.value })}
              />
              
              <input
                type="text"
                placeholder="Video URL (YouTube, Vimeo, etc.)"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              />
              
              <textarea
                placeholder="Spotify Embed Code (paste full iframe code)"
                value={formData.spotify_embed}
                onChange={(e) => setFormData({ ...formData, spotify_embed: e.target.value })}
                rows="3"
              />
              
              <input
                type="text"
                placeholder="External Link URL"
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              />
              
              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingPost ? 'Update' : 'Create'}
                </button>
                {editingPost && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setEditingPost(null);
                      setFormData({ title: '', content: '', featured_image: '', post_type: 'article', tags: '', spotify_embed: '', link_url: '', video_url: '', created_at: '' });
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
          
          {/* Posts List */}
          <div className="admin-posts">
            <h3>All Posts ({posts.length})</h3>
            <div className="posts-list">
              {posts.map((post) => (
                <div key={post.id} className="post-item">
                  <div className="post-item-content">
                    <h4>{post.title}</h4>
                    <small>{new Date(post.created_at).toLocaleDateString()}</small>
                  </div>
                  <div className="post-item-actions">
                    <button onClick={() => handleEdit(post)} className="edit-btn">Edit</button>
                    <button onClick={() => handleDelete(post.id)} className="delete-btn">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
